import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Row, Spinner } from '@themesberg/react-bootstrap';
import {
  FaExclamationTriangle,
  FaRedo,
  FaSearch,
  FaSyncAlt,
} from 'react-icons/fa';
import { get, post } from '../../../services/back.service';

/** Cada cuánto se pregunta si la consulta terminó. */
const POLL_MS = 5000;
/** Tope de espera: por encima de esto dejamos de preguntar y lo decimos. */
const MAX_WAIT_MS = 6 * 60 * 1000;

const STATUS_NONE = 'NONE';
const STATUS_IN_PROGRESS = 'IN_PROGRESS';
const STATUS_READY = 'READY';
const STATUS_ERROR = 'ERROR';

/** Niveles de certificacion definidos por tipo de servicio (ver lib/models.py). */
const LEVEL_NONE = 'NONE';
const LEVEL_BASIC = 'BASIC';
const LEVEL_PLUS = 'PLUS';
const LEVEL_PLUS_PA = 'PLUS_PA';

/** Como se informa al perito que se va a consultar. */
const LEVEL_LABEL = {
  [LEVEL_BASIC]: 'Consulta basica',
  [LEVEL_PLUS]: 'Consulta Plus',
  [LEVEL_PLUS_PA]: 'Consulta Plus con propietarios anteriores',
};

/**
 * Consulta de certificación vehicular al proveedor externo.
 *
 * Qué se consulta no lo decide el perito: lo define el nivel configurado en el
 * tipo de servicio. Por eso este panel no tiene casillas de opciones, y si el
 * tipo de servicio no tiene certificación no se renderiza nada.
 *
 * La consulta tarda varios minutos, así que el botón solo la dispara y esta
 * pantalla va preguntando el estado. Si hay fuentes que fallaron, ofrece
 * reintentarlas, que no tiene costo para el proveedor.
 */
function CertificarPanel({ id, placa }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  const pollRef = useRef(null);
  const waitedRef = useRef(0);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const checkStatus = useCallback(async () => {
    const response = await get(`/peritaje/certificar/${id}/estado`, {});
    if (!response.success) return;
    setState(response.data);
  }, [id]);

  const startPolling = useCallback(() => {
    stopPolling();
    waitedRef.current = 0;
    setTimedOut(false);
    pollRef.current = setInterval(async () => {
      waitedRef.current += POLL_MS;
      await checkStatus();
      if (waitedRef.current >= MAX_WAIT_MS) {
        setTimedOut(true);
        stopPolling();
      }
    }, POLL_MS);
  }, [checkStatus, stopPolling]);

  // Al abrir el peritaje se muestra lo que ya haya consultado antes.
  useEffect(() => {
    if (!id) return undefined;
    checkStatus();
    return stopPolling;
  }, [id, checkStatus, stopPolling]);

  const start = async (force = false) => {
    setBusy(true);
    const response = await post(`/peritaje/certificar/${id}`, { force });
    setBusy(false);

    if (!response.success) {
      setState((prev) => ({
        status: STATUS_ERROR,
        error: response.message,
        allowed_level: prev ? prev.allowed_level : undefined,
      }));
      return;
    }
    setState(response.data);
    if (response.data.status === STATUS_IN_PROGRESS) startPolling();
  };

  const retry = async () => {
    setBusy(true);
    const response = await post(`/peritaje/certificar/${id}/reintentar`, {});
    setBusy(false);
    if (!response.success) {
      setState((prev) => ({
        status: STATUS_ERROR,
        error: response.message,
        allowed_level: prev ? prev.allowed_level : undefined,
      }));
      return;
    }
    setState(response.data);
    if (response.data.status === STATUS_IN_PROGRESS) startPolling();
  };

  const status = state ? state.status : STATUS_NONE;
  const running = status === STATUS_IN_PROGRESS;
  const summary = state ? state.summary : null;
  const failed = summary ? summary.failed : [];
  const level = state ? state.allowed_level : undefined;

  // Todavía no sabemos el nivel: no se dibuja nada, para no pintar un botón
  // que al rato desaparezca.
  if (!id || level === undefined || level === null) return null;
  // El tipo de servicio no tiene certificación configurada: no hay botón.
  if (level === LEVEL_NONE) return null;

  return (
    <Card className="mb-3">
      <Card.Body>
        <Row className="align-items-center mb-3">
          <Col>
            <h5 className="mb-0">Certificación vehicular</h5>
            <small className="text-muted">
              Consulta las fuentes del proveedor para la placa {placa}.
            </small>
          </Col>
          <Col className="d-flex justify-content-end align-items-center gap-2">
            <Button
              variant="outline-primary"
              size="sm"
              aria-label="Consultar certificacion"
              onClick={() => start(false)}
              disabled={busy || running}
              title="Usa el resultado guardado si todavía está vigente"
            >
              <FaSearch className="me-1" /> Consultar
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              aria-label="Volver a consultar"
              onClick={() => start(true)}
              disabled={busy || running || status === STATUS_NONE}
              title="Vuelve a consultar todo, aunque haya un resultado guardado"
            >
              <FaRedo className="me-1" /> Volver a consultar
            </Button>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col>
            <span className="badge bg-info">{LEVEL_LABEL[level]}</span>
            <small className="text-muted ms-2">
              Definido por el tipo de servicio de este peritaje.
            </small>
          </Col>
        </Row>

        {running && (
          <div className="d-flex align-items-center mb-3 text-muted">
            <Spinner size="sm" className="me-2" animation="border" />
            Consultando el proveedor. Puede tardar un par de minutos; no hace falta
            que espere en esta pantalla.
          </div>
        )}

        {timedOut && (
          <div className="alert alert-warning">
            La consulta está demorando más de lo habitual. Podés seguir trabajando y
            volver a mirarla en un rato.
          </div>
        )}

        {status === STATUS_ERROR && state.error && (
          <div className="alert alert-danger">{state.error}</div>
        )}

        {status === STATUS_READY && summary && (
          <div>
            <div className="mb-3">
              {summary.has_risk ? (
                <span className="badge bg-danger me-2">Con alertas de riesgo</span>
              ) : (
                <span className="badge bg-success me-2">Sin alertas de riesgo</span>
              )}
              <span className="text-muted">
                {summary.ok_sources} de {summary.total_sources} fuentes consultadas.
              </span>
            </div>

            <Row className="mb-3">
              {summary.documents.map((document) => (
                <Col key={`${document.doc_type}-${document.value}`} md="6">
                  <div className="border rounded p-2">
                    <strong>{document.value}</strong>{' '}
                    <span className="text-muted small">({document.doc_type})</span>
                    <div className="text-muted small">
                      {document.sources} fuentes
                    </div>
                  </div>
                </Col>
              ))}
            </Row>

            {summary.warning.length > 0 && (
              <div className="alert alert-warning py-2">
                <strong>Advertencias:</strong>{' '}
                {summary.warning.map((item) => item.code).join(', ')}
              </div>
            )}

            {failed.length > 0 && (
              <div className="alert alert-secondary py-2">
                <div className="d-flex align-items-center flex-wrap">
                  <span className="me-2">
                    <FaExclamationTriangle className="me-1" />
                    {failed.length} fuente{failed.length > 1 ? 's' : ''} sin respuesta:{' '}
                    {failed.map((item) => item.code).join(', ')}
                  </span>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    className="ms-auto"
                    onClick={retry}
                    disabled={busy || running}
                    title="No tiene costo: el proveedor ya cobró esas fuentes"
                  >
                    <FaSyncAlt className="me-1" /> Reintentar sin costo
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
}

export default CertificarPanel;